import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { COMPANY, ROLE_LABEL, canViewHR } from "@/lib/helpers";
import { useTheme } from "@/lib/theme";
import CommandPalette from "@/components/CommandPalette";
import AiAssistantDrawer from "@/components/AiAssistantDrawer";
import {
  SquaresFour, Package, FileText, Receipt, ShoppingCart, Truck,
  Gear, ClipboardText, Bell, UsersThree, SignOut, List, X,
  Mountains, CalendarBlank, Buildings, Factory, Invoice, ListChecks,
  FolderOpen, GasPump, IdentificationBadge, Gear as GearIcon, Wrench, CurrencyCircleDollar,
  Function, Handshake, Clock as ClockIcon, Moon, Sun, Globe, EnvelopeSimple,
  MagnifyingGlass,
} from "@phosphor-icons/react";

const PROVIDER_NAV = {
  title: "LUROMA",
  items: [
    { to: "/organizations", icon: Globe, label: "Client Setup", test: "nav-organizations" },
    { to: "/leads", icon: EnvelopeSimple, label: "Leads Inbox", test: "nav-leads" },
    { to: "/notifications", icon: Bell, label: "Notifications", test: "nav-notifications" },
  ],
};

const NAV_GROUPS = [
  {
    title: "Overview",
    items: [
      { to: "/", end: true, icon: SquaresFour, label: "Dashboard", test: "nav-dashboard" },
      { to: "/notifications", icon: Bell, label: "Notifications", test: "nav-notifications" },
    ],
  },
  {
    title: "Production",
    items: [
      { to: "/production", icon: Mountains, label: "Production", test: "nav-production" },
      { to: "/projects", icon: FolderOpen, label: "Projects", test: "nav-projects" },
      { to: "/weekly-budget", icon: CalendarBlank, label: "Weekly Budget", test: "nav-budget" },
      { to: "/inventory", icon: Package, label: "Inventory", test: "nav-inventory" },
      { to: "/stock-take", icon: ListChecks, label: "Stock-Take", test: "nav-stocktake" },
      { to: "/stock-issuances", icon: Handshake, label: "Stores Issuance", test: "nav-issuances" },
    ],
  },
  {
    title: "Sales",
    items: [
      { to: "/invoices", icon: Invoice, label: "Invoices", test: "nav-invoices" },
      { to: "/quotations", icon: Receipt, label: "Quotations", test: "nav-quotations" },
      { to: "/orders", icon: ShoppingCart, label: "Orders", test: "nav-orders" },
      { to: "/customers", icon: Buildings, label: "Customers", test: "nav-customers" },
    ],
  },
  {
    title: "Purchasing",
    items: [
      { to: "/purchase-orders", icon: FileText, label: "Purchase Orders", test: "nav-po" },
      { to: "/requests", icon: ClipboardText, label: "Requests", test: "nav-requests" },
      { to: "/suppliers", icon: Factory, label: "Suppliers", test: "nav-suppliers" },
    ],
  },
  {
    title: "HR",
    hrOnly: true,
    items: [
      { to: "/hr", icon: IdentificationBadge, label: "HR / People", test: "nav-hr" },
      { to: "/rosters", icon: CalendarBlank, label: "Work Rosters", test: "nav-rosters" },
      { to: "/daily-hours", icon: ClockIcon, label: "Daily Hours", test: "nav-daily-hours" },
      { to: "/payroll", icon: CurrencyCircleDollar, label: "Payroll", test: "nav-payroll" },
    ],
  },
  {
    title: "Fleet",
    items: [
      { to: "/trucks", icon: Truck, label: "Trucks", test: "nav-trucks" },
      { to: "/fuel-logs", icon: GasPump, label: "Fuel & Diesel", test: "nav-fuel" },
      { to: "/machines", icon: Gear, label: "Machines", test: "nav-machines" },
      { to: "/maintenance-records", icon: Wrench, label: "Maintenance", test: "nav-maintenance" },
    ],
  },
  {
    title: "Finance",
    financeOnly: true,
    items: [
      { to: "/finance/coa", icon: CurrencyCircleDollar, label: "Chart of Accounts", test: "nav-coa" },
      { to: "/finance/journals", icon: FileText, label: "Journals", test: "nav-journals" },
      { to: "/finance/trial-balance", icon: Function, label: "Trial Balance", test: "nav-trial-balance" },
      { to: "/finance/ap", icon: Invoice, label: "Supplier Invoices", test: "nav-ap" },
      { to: "/finance/ar", icon: Receipt, label: "Customer Invoicing", test: "nav-ar" },
      { to: "/finance/bank-rec", icon: Buildings, label: "Bank Reconciliation", test: "nav-bank-rec" },
      { to: "/finance/fixed-assets", icon: Factory, label: "Fixed Assets", test: "nav-fa" },
      { to: "/finance/cost-intel", icon: Handshake, label: "Cost Intelligence", test: "nav-cost" },
    ],
  },
  {
    title: "Automation",
    autoOnly: true,
    items: [
      { to: "/automation", icon: Function, label: "Rules", test: "nav-automation" },
    ],
  },
  {
    title: "Settings",
    items: [
      { to: "/users", icon: UsersThree, label: "Users", test: "nav-users", adminOnly: true },
      { to: "/activity", icon: ClockIcon, label: "Activity Log", test: "nav-activity", auditOnly: true },
      { to: "/settings", icon: GearIcon, label: "Settings", test: "nav-settings", adminOnly: true },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Global ⌘K / Ctrl+K to open the search palette
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Apply tenant branding at runtime — sets CSS variables + accent color
  useEffect(() => {
    const primary = user?.org?.brand_primary || "#002FA7";
    const accent = user?.org?.brand_accent || "#FF6A00";
    document.documentElement.style.setProperty("--brand-primary", primary);
    document.documentElement.style.setProperty("--brand-accent", accent);
  }, [user?.org?.brand_primary, user?.org?.brand_accent]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setUnread(data.filter((n) => !n.read).length);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    }
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  async function onLogout() {
    await logout();
    nav("/login");
  }

  if (!user) return null;

  const isProvider = user.role === "luroma_provider";
  const orgName = user?.org?.name || "LUROMA";
  const orgCode = user?.org?.code || "";
  const brandPrimary = user?.org?.brand_primary || "#002FA7";
  const logoUrl = user?.org?.logo_url;

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-white border-r border-[#e4e4e7] flex flex-col transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="p-5 border-b border-[#e4e4e7] dark:border-[#27272a] flex items-center gap-3">
          {isProvider ? (
            <>
              <img
                src="/assets/luroma_logo.png"
                alt="LUROMA"
                data-testid="sidebar-logo"
                className="h-10 w-auto object-contain"
              />
              <div>
                <div className="font-heading text-base font-black tracking-tighter leading-none dark:text-white">
                  LUROMA
                </div>
                <div className="phizo-label text-[10px] mt-1">Provider Console</div>
              </div>
            </>
          ) : (
            <>
              {logoUrl ? (
                <img src={logoUrl} alt={orgName} className="h-10 w-10 object-contain" data-testid="sidebar-logo" />
              ) : (
                <div
                  className="h-10 w-10 flex items-center justify-center font-black text-white rounded"
                  style={{ background: brandPrimary }}
                  data-testid="sidebar-logo"
                >
                  {(orgCode || orgName)?.[0] || "P"}
                </div>
              )}
              <div>
                <div className="font-heading text-base font-black tracking-tighter leading-none dark:text-white">
                  {orgName?.toUpperCase() || "CONTROL ROOM"}
                </div>
                <div className="phizo-label text-[10px] mt-1">Control Room</div>
              </div>
            </>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {isProvider ? (
            <div data-testid="nav-group-luroma">
              <div className="phizo-label text-[10px] px-3 mb-1.5 text-[#a1a1aa]">
                {PROVIDER_NAV.title}
              </div>
              <div className="space-y-0.5">
                {PROVIDER_NAV.items.map((n) => {
                  const Icon = n.icon;
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={n.test}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                          isActive
                            ? "bg-[#09090b] text-white"
                            : "text-[#52525b] hover:bg-[#f4f4f5] hover:text-[#09090b]"
                        }`
                      }
                    >
                      <Icon size={18} weight="bold" />
                      <span className="flex-1">{n.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ) : NAV_GROUPS.map((group) => {
            if (group.hrOnly && !canViewHR(user.role)) return null;
            if (group.financeOnly && !["admin", "director", "finance", "manager"].includes(user.role)) return null;
            if (group.autoOnly && !["admin", "director"].includes(user.role)) return null;
            const auditRoles = ["admin", "director", "hr", "finance"];
            const visible = group.items.filter((n) => {
              if (n.adminOnly && user.role !== "admin") return false;
              if (n.auditOnly && !auditRoles.includes(user.role)) return false;
              return true;
            });
            if (visible.length === 0) return null;
            return (
              <div key={group.title} data-testid={`nav-group-${group.title.toLowerCase()}`}>
                <div className="phizo-label text-[10px] px-3 mb-1.5 text-[#a1a1aa]">
                  {group.title}
                </div>
                <div className="space-y-0.5">
                  {visible.map((n) => {
                    const Icon = n.icon;
                    return (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.test}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                            isActive
                              ? "bg-[#09090b] text-white"
                              : "text-[#52525b] hover:bg-[#f4f4f5] hover:text-[#09090b]"
                          }`
                        }
                      >
                        <Icon size={18} weight="bold" />
                        <span className="flex-1">{n.label}</span>
                        {n.to === "/notifications" && unread > 0 ? (
                          <span className="bg-[#FF2A2A] text-white text-[10px] font-bold px-1.5 py-0.5">
                            {unread}
                          </span>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#e4e4e7]">
          <NavLink
            to={`/users/${user.id}`}
            data-testid="my-profile-link"
            className="flex items-center gap-3 mb-3 hover:bg-[#f4f4f5] p-1 -mx-1 transition-colors"
          >
            <div className="h-9 w-9 bg-[#002FA7] text-white flex items-center justify-center font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#09090b] truncate" data-testid="user-name">
                {user.name}
              </div>
              <div className="phizo-label text-[10px]" data-testid="user-role">
                {ROLE_LABEL[user.role] || user.role}
              </div>
            </div>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 border border-[#e4e4e7] py-2 text-sm font-medium text-[#52525b] hover:bg-[#09090b] hover:text-white hover:border-[#09090b] transition-colors"
          >
            <SignOut size={16} weight="bold" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white border-b border-[#e4e4e7] h-14 flex items-center gap-3 px-4 lg:px-8">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={20} /> : <List size={20} />}
          </button>
          <div className="phizo-label">{COMPANY.tagline}</div>
          <div className="ml-auto flex items-center gap-4">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              data-testid="open-command-palette"
              title="Search everything (⌘K)"
              className="hidden sm:flex items-center gap-2 border border-[#e4e4e7] bg-white text-[#52525b] hover:text-[#09090b] hover:border-[#09090b] px-3 py-1.5 text-xs"
            >
              <MagnifyingGlass size={14} weight="bold" />
              <span>Search</span>
              <kbd className="ml-1 text-[9px] border border-[#e4e4e7] px-1 mono">⌘K</kbd>
            </button>
            <div className="hidden sm:block text-xs text-[#52525b] mono">
              {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              title={isDark ? "Switch to day mode" : "Switch to night mode"}
              className="p-1 text-[#52525b] hover:text-[#09090b]"
            >
              {isDark ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
            </button>
            <NavLink to="/notifications" className="relative p-1 text-[#52525b] hover:text-[#09090b]">
              <Bell size={20} weight="bold" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF2A2A] text-white text-[9px] font-bold px-1 py-0.5">
                  {unread}
                </span>
              )}
            </NavLink>
          </div>
        </header>

        <div className="flex-1 p-4 lg:p-8 bg-[#fafafa] min-h-[calc(100vh-3.5rem)] text-[#09090b]">
          <Outlet />
        </div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {!isProvider && <AiAssistantDrawer />}
    </div>
  );
}
